import React, { useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Num, Ring, Spark, useReady } from "./adminCharts";
import { CATEGORIES } from "./categories";
import useWidgetLayout from "../widgets/useWidgetLayout";
import { CustomizeButtons, EditBar, SaveErrorBar, WidgetGrid, WidgetLibrary } from "../widgets/WidgetGrid";
import { ADMIN_DEFAULTS, ADMIN_WIDGETS, RANGE_WINDOWS } from "./adminWidgets";
import "./adminDashboard.css";

export const RANGES = Object.keys(RANGE_WINDOWS);
export { CATEGORIES };

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function Hero({ data, ready, range, onRange, L }) {
  const { header, kpis } = data;
  const maxMini = Math.max(1, ...kpis.cohortMini);
  return (
    <header className="ad-hero" data-testid="admin-hero"><div className="ad-hero-in">
      <div className="ad-top">
        <div className="t">
          <span className="eb">{header.eyebrow}</span>
          <h1>{header.greeting}</h1>
          <p>Overseeing <b>{plural(kpis.cohorts, "active cohort")}</b> and <b>{plural(kpis.enrollment, "learner")}</b></p>
        </div>
        {!L.edit && (
          <div className="ad-range dk" role="group" aria-label="Chart range">
            {RANGES.map((x) => <button key={x} type="button" aria-pressed={range === x} className={range === x ? "on" : ""} onClick={() => onRange(x)}>{x}</button>)}
          </div>
        )}
        <CustomizeButtons L={L} dark />
      </div>
      <div className="hm4">
        <div className="hk" data-testid="admin-kpi-enrollment">
          <span className="eb">Total enrollment</span>
          <div className="row"><span className="v"><Num to={kpis.enrollment} /></span><Spark data={kpis.enrollmentSpark} color="var(--color-accent-lime)" w={96} h={32} delay={300} /></div>
          <span className="sub">{kpis.enrollmentDelta != null ? <><span className="dl">{kpis.enrollmentDelta >= 0 ? "+" : ""}{kpis.enrollmentDelta}%</span>vs. prior 6 months</> : "Across active cohorts"}</span>
        </div>
        <div className="hk" data-testid="admin-kpi-cohorts" style={{ animationDelay: "60ms" }}>
          <span className="eb">Active cohorts</span>
          <div className="row"><span className="v"><Num to={kpis.cohorts} delay={60} /></span>
            <div className="hmini" aria-hidden="true">{kpis.cohortMini.map((m, i) => <i key={i} className={i === kpis.cohortMini.length - 1 ? "on" : ""} style={{ height: ready ? `${m / maxMini * 100}%` : 0, transitionDelay: `${i * 70}ms` }}></i>)}</div></div>
          <span className="sub">{kpis.upcomingStarts} upcoming starts in the next 30 days</span>
        </div>
        <div className="hk" data-testid="admin-kpi-certs" style={{ animationDelay: "120ms" }}>
          <span className="eb">Instructor certs expiring</span>
          <div className="row"><span className="v"><Num to={kpis.certs} delay={120} /></span><Spark data={kpis.certsSpark} color="var(--color-accent-pink)" w={96} h={32} delay={420} /></div>
          <span className="sub"><span className="dl w">{kpis.certsSoon}</span>expire in 14 days · {kpis.certs} in 90</span>
        </div>
        <div className="hk" data-testid="admin-kpi-compliance" style={{ animationDelay: "180ms" }}>
          <span className="eb">Branch compliance</span>
          <div className="row"><span className="v">{kpis.compliance != null ? <Num to={kpis.compliance} dec={1} suf="%" delay={180} /> : "—"}</span>
            {kpis.compliance != null && <Ring pct={kpis.compliance} size={44} sw={5} color="var(--color-accent-lime)" delay={350} />}</div>
          <span className="sub">{kpis.compliance != null ? `Across ${kpis.graded} graded sheets` : "No checklists graded yet"}</span>
        </div>
      </div>
    </div></header>
  );
}

/**
 * Global-admin / admin landing dashboard: a dark KPI hero plus a customizable widget grid.
 * @param {{ data: object, raw: object, tenantId: string, isGlobalAdmin?: boolean, now?: Date }} props
 *   `data` is the buildAdminModel view model; `raw` carries the fetched submissions for the approvals widget.
 */
export default function AdminDashboard({ data, raw = { submissions: [] }, tenantId, isGlobalAdmin = false, now: nowProp }) {
  const [range, setRange] = useState("12M");
  const ready = useReady(250);
  const now = useMemo(() => nowProp || new Date(), [nowProp]);
  const L = useWidgetLayout({ role: "admin", tenantId, registry: ADMIN_WIDGETS, defaults: ADMIN_DEFAULTS });
  const ctx = useMemo(() => ({ m: data, raw, tenantId, isGlobalAdmin, now, range, navigate: data.navigate }), [data, raw, tenantId, isGlobalAdmin, now, range]);
  if (data.loading || L.loading) return <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>;
  return (
    <div className="ad" data-testid="admin-dashboard">
      <Hero data={data} ready={ready} range={range} onRange={setRange} L={L} />
      <main className="ad-wrap">
        <div className="ad-grid">
          <SaveErrorBar L={L} />
          <EditBar L={L} />
          <WidgetGrid L={L} ctx={ctx} />
        </div>
      </main>
      <WidgetLibrary L={L} roleLabel="Administrator" ctx={ctx} />
    </div>
  );
}
